import React, { memo } from 'react';
import { Users, Pin, VolumeX, Volume2, PinOff } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar } from './Avatar';

export const ConversationItem = memo(function ConversationItem({ 
  conversation, 
  isSelected, 
  currentUserId,
  isDark,
  onSelect,
  onTogglePin,
  onToggleMute,
  formatTime
}) {
  const getConversationName = () => {
    if (conversation.type === 'group') return conversation.name;
    const other = conversation.participant_names?.find(p => p.id !== currentUserId);
    return other ? `${other.prenom} ${other.nom}` : 'Conversation';
  };

  const getConversationAvatar = () => {
    if (conversation.type === 'group') {
      return (
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
          <Users size={20} />
        </div>
      );
    }
    const other = conversation.participant_names?.find(p => p.id !== currentUserId);
    return <Avatar user={other} size="md" />;
  };

  return (
    <div
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 p-4 cursor-pointer transition-colors border-b relative group',
        isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50',
        isSelected && (isDark ? 'bg-white/10' : 'bg-red-50')
      )}
      data-testid={`conversation-${conversation.id}`}
    >
      {/* Indicateurs en haut à droite */}
      <div className="absolute top-1 right-1 flex items-center gap-1">
        {conversation.is_muted && (
          <VolumeX size={12} className={isDark ? "text-zinc-400" : "text-gray-400"} />
        )}
        {conversation.is_pinned && (
          <Pin size={12} className={isDark ? "text-red-400" : "text-red-500"} />
        )}
      </div>
      
      {getConversationAvatar()}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={cn(
            'font-medium truncate flex items-center gap-1',
            isDark ? 'text-white' : 'text-gray-900'
          )}>
            {getConversationName()}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Bouton sourdine (visible au hover) */}
            <button
              onClick={(e) => onToggleMute(e)}
              className={cn(
                'p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all bg-transparent',
                conversation.is_muted 
                  ? (isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-500 hover:text-amber-600') 
                  : (isDark ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-400 hover:text-gray-500')
              )}
              title={conversation.is_muted ? 'Réactiver les notifications' : 'Mettre en sourdine'}
            >
              {conversation.is_muted ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            
            {/* Bouton épingler (visible au hover) */}
            <button
              onClick={(e) => onTogglePin(e)}
              className={cn(
                'p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all bg-transparent',
                conversation.is_pinned 
                  ? (isDark ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600') 
                  : (isDark ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-400 hover:text-gray-500')
              )}
              title={conversation.is_pinned ? 'Désépingler' : 'Épingler'}
            >
              {conversation.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
            </button>
            
            {conversation.last_message_at && (
              <span className={cn(
                'text-xs ml-1',
                isDark ? 'text-zinc-500' : 'text-gray-500'
              )}>
                {formatTime(conversation.last_message_at)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className={cn(
            'text-sm truncate',
            isDark ? 'text-zinc-400' : 'text-gray-500'
          )}>
            {conversation.last_message || 'Nouvelle conversation'}
          </p>
          {conversation.unread_count > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full flex-shrink-0">
              {conversation.unread_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});
